/**
 * HamburgerMenu Component
 *
 * An animated menu toggle button with gradient background.
 * Displays hamburger icon when closed and close (X) icon when open.
 * Features smooth rotation animation on state change.
 *
 * Features:
 * - Gradient background (yellow to azur)
 * - Animated icon rotation on open/close
 * - Hover effects (brightness + scale)
 * - Keyboard accessible
 *
 * Ported from Vue HamburgerMenu.vue
 *
 * @example
 * ```tsx
 * const [menuOpen, setMenuOpen] = useState(false);
 *
 * <HamburgerMenu
 *   open={menuOpen}
 *   onToggle={() => setMenuOpen(!menuOpen)}
 * />
 * ```
 */

import React from "react";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { HamburgerMenuProps } from "./types";
import styles from "./HamburgerMenu.module.css";

export const HamburgerMenu = React.forwardRef<
  HTMLButtonElement,
  HamburgerMenuProps
>(
  (
    {
      open,
      onToggle,
      "aria-controls": ariaControls,
      className = "",
      "data-testid": testId,
    },
    ref,
  ) => {
    // Handle keyboard events
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle();
      }
    };

    // Build class names
    const containerClasses = cx(styles.icon, open && styles.open, className);

    return (
      <button
        ref={ref}
        type="button"
        className={containerClasses}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={open}
        aria-controls={ariaControls}
        aria-label={open ? "Close menu" : "Open menu"}
        data-testid={testId}
      >
        <Icon
          name={open ? "close" : "menu"}
          size="lg"
          className={styles.iconColor}
          aria-hidden={true}
        />
      </button>
    );
  },
);

HamburgerMenu.displayName = "HamburgerMenu";
